import { useEffect, useRef } from "react";
import { containRect } from "../../utils/videoContainCoords";
import {
  drawElbowOverlayOnCanvas,
  drawKneeOverlayOnCanvas,
  poseSideHasJoints,
} from "../../utils/strokeSlowMoKneeOverlay";
import { LEFT_COLOR, RIGHT_COLOR } from "./PoseSkeletonOverlay";
import "./poseOverlays.css";

function rectValid(r) {
  return (
    r &&
    Number.isFinite(Number(r.x)) &&
    Number.isFinite(Number(r.y)) &&
    Number(r.w) > 0 &&
    Number(r.h) > 0
  );
}

function drawSelectionRoi(ctx, roi, color, rect, label) {
  if (!rectValid(roi) || !rect) return;
  const { x: ox, y: oy, w, h } = rect;
  const x = ox + Number(roi.x) * w;
  const y = oy + Number(roi.y) * h;
  const bw = Number(roi.w) * w;
  const bh = Number(roi.h) * h;
  const strokeW = Math.max(1.5, Math.min(w, h) * 0.003);
  ctx.save();
  ctx.fillStyle = `${color}14`;
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeW;
  ctx.setLineDash([strokeW * 4, strokeW * 2.5]);
  ctx.fillRect(x, y, bw, bh);
  ctx.strokeRect(x, y, bw, bh);
  ctx.setLineDash([]);
  if (label) {
    const fontSize = Math.max(11, Math.min(w, h) * 0.028);
    ctx.font = `700 ${fontSize}px sans-serif`;
    ctx.fillStyle = color;
    ctx.textBaseline = "bottom";
    const ty = Math.max(oy + fontSize + 2, y - 4);
    ctx.fillText(label, x + strokeW, ty);
  }
  ctx.restore();
}

/**
 * Paused-frame knees or elbows overlay (arcs + degree labels).
 * Parent should share the same CSS flip/rotate as the <video> element.
 * Default `poseSide="left"` matches eval/replay coaching focus (Player A).
 */
export function JointAngleOverlay({
  sample,
  mode = "off",
  videoWidth = 0,
  videoHeight = 0,
  visible = true,
  poseSide = "left",
  emphasize = true,
  /** Optional coach-selected player box (normalized) shown under the arcs. */
  selectionRoi = null,
  selectionLabel = null,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !visible || (mode !== "knees" && mode !== "elbows")) {
      return undefined;
    }

    const paint = () => {
      const cssW = Math.max(1, canvas.clientWidth || 1);
      const cssH = Math.max(1, canvas.clientHeight || 1);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const bufW = Math.round(cssW * dpr);
      const bufH = Math.round(cssH * dpr);
      if (canvas.width !== bufW || canvas.height !== bufH) {
        canvas.width = bufW;
        canvas.height = bufH;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, bufW, bufH);

      const rect = containRect(cssW, cssH, videoWidth, videoHeight);
      const videoRect = {
        x: rect.x * dpr,
        y: rect.y * dpr,
        w: rect.w * dpr,
        h: rect.h * dpr,
      };
      const sideColor = poseSide === "right" ? RIGHT_COLOR : LEFT_COLOR;
      if (rectValid(selectionRoi)) {
        drawSelectionRoi(
          ctx,
          selectionRoi,
          sideColor,
          videoRect,
          selectionLabel
        );
      }
      const draw =
        mode === "elbows" ? drawElbowOverlayOnCanvas : drawKneeOverlayOnCanvas;
      const drawOpts = { emphasize };
      const left = sample?.left;
      const right = sample?.right;

      if (poseSide === "left") {
        if (poseSideHasJoints(left)) {
          draw(ctx, left, {}, null, videoRect, LEFT_COLOR, drawOpts);
        } else if (poseSideHasJoints(sample) && !sample?.right) {
          draw(ctx, sample, {}, null, videoRect, LEFT_COLOR, drawOpts);
        }
        return;
      }

      if (poseSide === "right") {
        if (poseSideHasJoints(right)) {
          draw(ctx, right, {}, null, videoRect, RIGHT_COLOR, drawOpts);
        }
        return;
      }

      const drewSplit =
        poseSideHasJoints(left) || poseSideHasJoints(right);
      if (drewSplit) {
        if (poseSideHasJoints(left)) {
          draw(ctx, left, {}, null, videoRect, LEFT_COLOR, drawOpts);
        }
        if (poseSideHasJoints(right)) {
          draw(ctx, right, {}, null, videoRect, RIGHT_COLOR, drawOpts);
        }
      } else if (poseSideHasJoints(sample)) {
        draw(ctx, sample, {}, null, videoRect, LEFT_COLOR, drawOpts);
      }
    };
    paint();
    const ro = new ResizeObserver(() => paint());
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [
    sample,
    mode,
    videoWidth,
    videoHeight,
    visible,
    poseSide,
    emphasize,
    selectionRoi,
    selectionLabel,
  ]);

  if (!visible || (mode !== "knees" && mode !== "elbows")) return null;

  return (
    <canvas
      ref={canvasRef}
      className="pose-skeleton-overlay"
      aria-hidden
    />
  );
}

export default JointAngleOverlay;
